import React, { useState, useEffect } from 'react';
import { Utensils, Calendar, Clock, Send, X, Loader, AlertCircle } from 'lucide-react';
import { sendDinnerRecommendation } from '../lib/dinnerRecommendations';
import { supabase } from '../lib/supabase';

interface DinnerRecommendationButtonProps {
  userId: string;
  userName?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const DinnerRecommendationButton: React.FC<DinnerRecommendationButtonProps> = ({
  userId,
  userName,
  onSuccess,
  onError
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [formData, setFormData] = useState({
    message: '',
    date: '',
    time: '',
    restaurantId: '',
    barId: ''
  });
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [bars, setBars] = useState<any[]>([]);

  useEffect(() => {
    checkAuth();
    if (isOpen) {
      fetchVenues();
    }
  }, [isOpen]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    } catch (err) {
      console.error('Error checking auth:', err);
      setIsAuthenticated(false);
    }
  };

  const fetchVenues = async () => {
    try {
      // Fetch restaurants
      const { data: restaurantsData, error: restaurantsError } = await supabase
        .from('restaurants')
        .select('id, name')
        .eq('status', 'approved')
        .order('name');

      if (restaurantsError) throw restaurantsError;
      setRestaurants(restaurantsData || []);

      // Fetch bars
      const { data: barsData, error: barsError } = await supabase
        .from('bars')
        .select('id, name')
        .eq('status', 'approved')
        .order('name');

      if (barsError) throw barsError;
      setBars(barsData || []);
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError('Failed to load venues');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear the other venue type if one is selected
    if (name === 'restaurantId' && value) {
      setFormData(prev => ({ ...prev, barId: '' }));
    } else if (name === 'barId' && value) {
      setFormData(prev => ({ ...prev, restaurantId: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);

      await sendDinnerRecommendation(
        userId,
        formData.message,
        formData.restaurantId || undefined,
        formData.barId || undefined,
        formData.date || undefined,
        formData.time || undefined
      );

      setIsOpen(false);
      setFormData({
        message: '',
        date: '',
        time: '',
        restaurantId: '',
        barId: ''
      });
      
      onSuccess?.();
    } catch (err) {
      console.error('Error sending dinner recommendation:', err);
      setError(err instanceof Error ? err.message : 'Failed to send dinner recommendation');
      onError?.(err instanceof Error ? err : new Error('Failed to send dinner recommendation'));
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <button 
        disabled
        className="flex-1 flex items-center justify-center space-x-1 bg-gray-300 text-white px-3 py-2 rounded-md cursor-not-allowed"
      >
        <Utensils className="h-4 w-4" />
        <span>Sign in to Recommend Dinner</span>
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex-1 flex items-center justify-center space-x-1 bg-green-500 text-white px-3 py-2 rounded-md hover:bg-green-600 transition"
      >
        <Utensils className="h-4 w-4" />
        <span>Recommend Dinner</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Recommend Dinner to {userName || 'User'}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {error && (
              <div className="m-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md text-gray-900"
                  placeholder="Let's have dinner together!"
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>Date (Optional)</span>
                    </div>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>Time (Optional)</span>
                    </div>
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md text-gray-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Restaurant (Optional)
                </label>
                <select
                  name="restaurantId"
                  value={formData.restaurantId}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md text-gray-900"
                  disabled={!!formData.barId}
                >
                  <option value="">Select a restaurant</option>
                  {restaurants.map(restaurant => (
                    <option key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bar (Optional)
                </label>
                <select
                  name="barId"
                  value={formData.barId}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md text-gray-900"
                  disabled={!!formData.restaurantId}
                >
                  <option value="">Select a bar</option>
                  {bars.map(bar => (
                    <option key={bar.id} value={bar.id}>
                      {bar.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Send Recommendation</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default DinnerRecommendationButton;